import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface AsideComponentProps {
  title: string;
  children: React.ReactNode;
  isVisible: boolean;
  onClose: () => void;
}

const AsideComponent: React.FC<AsideComponentProps> = ({
  title,
  children,
  isVisible,
  onClose,
}) => {
  const asideRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible) {
      gsap.to(overlayRef.current, { autoAlpha: 1, duration: 0.3 }); // Fade in overlay
      gsap.to(asideRef.current, {
        x: 0,
        duration: 0.5,
        ease: "power3.out", // Slide in aside from the right
      });
    } else {
      gsap.to(asideRef.current, {
        x: "100%",
        duration: 0.5,
        ease: "power3.in",
        onComplete: () => {
          gsap.to(overlayRef.current, { autoAlpha: 0, duration: 0.3 }); // Fade out overlay
        },
      });
    }
  }, [isVisible]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      asideRef.current &&
      !asideRef.current.contains(event.target as Node) &&
      overlayRef.current &&
      overlayRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Disable scrolling when aside is open
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto"; // Re-enable scrolling
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto"; // Clean up on component unmount
    };
  }, [isVisible]);

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-gray-900/50 pointer-events-auto"
        style={{ visibility: "hidden", opacity: 0 }}
      />
      <div
        ref={asideRef}
        className="absolute right-0 top-0 bottom-0 bg-white w-3/4 md:w-1/3 shadow-lg transform translate-x-full pointer-events-auto"
        style={{ transform: "translateX(100%)" }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            className="text-gray-500 hover:text-gray-800 text-3xl"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

export default AsideComponent;
